(() => {
  if (customElements.get('matilde-cart')) return;
  class MatildeCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.syncViewport = () => {
        // Native dynamic viewport units avoid stale visualViewport heights on iOS.
        if (CSS.supports('height', '100dvh')) return;
        const viewport = window.visualViewport;
        this.dialog.style.setProperty('--mc-viewport-height', `${viewport?.height || window.innerHeight}px`);
        this.dialog.style.setProperty('--mc-viewport-top', `${viewport?.offsetTop || 0}px`);
      };
      this.root = this.dataset.root || '/';
      this.section = this.dataset.section;
      this.busy = false;
      this.quantityChanges = new Map();
      this.promotionLevel = Math.min(3, Math.max(0, Number(this.content.dataset.discountQuantity || 0) - 1));
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('mc-open');
        if (this.mobileThemeMeta) {
          if (this.previousThemeColor === null) this.mobileThemeMeta.removeAttribute('content');
          else this.mobileThemeMeta.setAttribute('content', this.previousThemeColor);
          this.mobileThemeMeta = null;
        }
        clearInterval(this.timer);
        window.removeEventListener('resize', this.syncViewport);
        window.visualViewport?.removeEventListener('resize', this.syncViewport);
        window.visualViewport?.removeEventListener('scroll', this.syncViewport);
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      });
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right) this.close();
        }
      });
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),[tabindex="0"]')].filter(element => element.getClientRects().length > 0);
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      });
      this.addEventListener('click', event => this.handleClick(event));
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-mc-related-variant]')) return;
        const card = event.target.closest('[data-mc-related-card]');
        card.querySelector('[data-mc-related-price]').textContent = event.target.selectedOptions[0].dataset.price;
      });
      document.addEventListener('click', event => {
        const link = event.target.closest('a[href],button[data-cart-toggle],.js-drawer-open-cart');
        if (!link || this.contains(link) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
        const url = link.tagName === 'A' ? new URL(link.href, location.origin) : null;
        if (link.matches('.js-drawer-open-cart,[data-cart-toggle]') || (url?.origin === location.origin && /\/cart\/?$/.test(url.pathname))) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.open(link);
          this.run(async () => { await this.refresh(); await this.normalizeProtection(); });
        }
      }, true);
      document.addEventListener('submit', event => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !/\/cart\/add\/?$/.test(new URL(form.action, location.origin).pathname)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.busy) return;
        if (typeof this.dialog.showModal !== 'function') {
          HTMLFormElement.prototype.submit.call(form);
          return;
        }
        const button = event.submitter || form.querySelector('[type=submit]');
        const data = new FormData(form);
        data.set('sections', this.section);
        data.set('sections_url', location.pathname);
        this.open(button);
        this.run(async () => {
          const response = await this.request('cart/add.js', data);
          await this.reconcile(response);
        });
      }, true);
      document.addEventListener('ajaxProduct:added', event => {
        this.open(event.detail?.addToCartBtn);
        this.run(() => this.refresh());
      });
      document.addEventListener('cart:open', () => { this.open(); this.run(async () => { await this.refresh(); await this.normalizeProtection(); }); });
      document.addEventListener('cart:close', () => this.close());
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && this.dialog.open && !this.busy) this.run(() => this.refresh());
      });
    }
    get content() { return this.querySelector('[data-mc-content]'); }
    get scroller() { return this.querySelector('.mc__scroll') || this.content; }
    get total() { return Number(this.content.dataset.total); }
    get goal() { return Number(this.content.dataset.goal); }
    get protectionLines() { return JSON.parse(this.content.dataset.protectionLines || '[]'); }
    open(opener) {
      if (typeof this.dialog.showModal !== 'function') {
        location.assign(this.root + 'cart');
        return;
      }
      if (!this.dialog.open) {
        this.opener = opener || document.activeElement;
        if (window.matchMedia('(max-width: 768px)').matches) {
          this.mobileThemeMeta = document.querySelector('meta[name="theme-color"]');
          if (this.mobileThemeMeta) {
            this.previousThemeColor = this.mobileThemeMeta.getAttribute('content');
            this.mobileThemeMeta.setAttribute('content', '#ffffff');
          }
        }
        this.syncViewport();
        window.addEventListener('resize', this.syncViewport);
        window.visualViewport?.addEventListener('resize', this.syncViewport);
        window.visualViewport?.addEventListener('scroll', this.syncViewport);
        this.dialog.showModal();
        document.documentElement.classList.add('mc-open');
        this.querySelector('[data-mc-close]').focus({ preventScroll: true });
      }
      this.startTimer();
    }
    close() { this.dialog.close(); }
    error(message = '') {
      const element = this.querySelector('[data-mc-error]');
      element.textContent = message;
      element.hidden = !message;
    }
    async run(action) {
      if (this.busy) return;
      this.busy = true;
      this.error();
      this.setBusy(true);
      try { await action(); }
      catch (error) {
        this.error(error.message || 'Handlekurven kunne ikke oppdateres. Prøv igjen.');
        try { await this.refresh(false); } catch (_) { /* Keep the last confirmed cart visible. */ }
      } finally {
        this.busy = false;
        this.setBusy(false);
        this.pendingFocus?.();
        this.pendingFocus = null;
        if (this.quantityChanges.size) this.flushQuantityChanges();
      }
    }
    queueQuantityChange(item, delta, remove = false) {
      const identity = item?.dataset.mcIdentity;
      if (!identity) return;
      const change = this.quantityChanges.get(identity) || { delta: 0, remove: false };
      change.delta += delta;
      change.remove = change.remove || remove;
      this.quantityChanges.set(identity, change);
      this.showPendingQuantities();
      this.setBusy(true);
      clearTimeout(this.quantityTimer);
      this.quantityTimer = setTimeout(() => this.flushQuantityChanges(), 90);
    }
    showPendingQuantities() {
      for (const row of this.content.querySelectorAll('[data-mc-identity]')) {
        const change = this.quantityChanges.get(row.dataset.mcIdentity);
        if (!change) continue;
        const count = row.querySelector('.mc__quantity span');
        if (count) count.textContent = change.remove ? '0' : String(Math.max(0, Number(row.dataset.quantity) + change.delta));
      }
    }
    flushQuantityChanges() {
      if (this.busy || !this.quantityChanges.size) return;
      clearTimeout(this.quantityTimer);
      const changes = this.quantityChanges;
      this.quantityChanges = new Map();
      const updates = {};
      for (const [identity, change] of changes) {
        const row = [...this.content.querySelectorAll('[data-mc-identity]')].find(item => item.dataset.mcIdentity === identity);
        if (!row) continue;
        const lines = this.mcGetGroupedLines?.(row) || [{ key: row.dataset.key, quantity: Number(row.dataset.quantity) }];
        let remaining = change.delta;
        if (change.remove) {
          for (const line of lines) updates[line.key] = 0;
        } else if (remaining >= 0) {
          updates[lines[0].key] = Number(lines[0].quantity) + remaining;
        } else {
          for (const line of [...lines].reverse()) {
            const removed = Math.min(Number(line.quantity), -remaining);
            updates[line.key] = Number(line.quantity) - removed;
            remaining += removed;
            if (!remaining) break;
          }
        }
      }
      if (!Object.keys(updates).length) { this.setBusy(false); return; }
      const entries = Object.entries(updates);
      this.run(async () => this.reconcile(await this.request(
        entries.length === 1 ? 'cart/change.js' : 'cart/update.js',
        entries.length === 1 ? { id: entries[0][0], quantity: entries[0][1] } : { updates }
      )));
    }
    setBusy(busy) {
      this.dialog.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('button:not([data-mc-close]):not([data-mc-quantity]):not([data-mc-remove]),input,select').forEach(element => {
        if (busy && !element.disabled) {
          element.dataset.mcBusyDisabled = '';
          element.disabled = true;
        } else if (!busy && element.hasAttribute('data-mc-busy-disabled')) {
          element.disabled = false;
          delete element.dataset.mcBusyDisabled;
        }
      });
      const checkout = this.querySelector('[data-mc-checkout]');
      if (checkout) checkout.setAttribute('aria-disabled', String(busy));
    }
    async fetchText(url, options = {}) {
      // AbortController also works on mobile browsers without AbortSignal.timeout.
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const text = await response.text();
        return { response, text };
      } finally {
        clearTimeout(timeout);
      }
    }
    async request(path, data) {
      const options = { credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
      if (data) {
        options.method = 'POST';
        if (data instanceof FormData) options.body = data;
        else {
          options.headers['Content-Type'] = 'application/json';
          options.body = JSON.stringify({ ...data, sections: this.section, sections_url: location.pathname });
        }
      }
      let response, text;
      try { ({ response, text } = await this.fetchText(this.root + path, options)); }
      catch (error) {
        if (error.name === 'TimeoutError' || error.name === 'AbortError') throw new Error('Tilkoblingen til butikken tok for lang tid. Prøv igjen.');
        throw new Error('Kunne ikke koble til butikken. Kontroller forbindelsen og prøv igjen.');
      }
      if (response.status === 429) throw new Error(this.dataset.rateLimitMessage);
      let result;
      try { result = JSON.parse(text); }
      catch (_) { throw new Error('Butikken ga et uventet svar. Last siden på nytt og prøv igjen.'); }
      if (!result || typeof result !== 'object') throw new Error('Handlekurven kunne ikke leses. Last siden på nytt og prøv igjen.');
      if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || 'Handlekurven kunne ikke oppdateres. Prøv igjen.');
      return result;
    }
    async refresh(celebrate = true) {
      const url = new URL(location.pathname, location.origin);
      url.searchParams.set('section_id', this.section);
      url.searchParams.set('_mc', Date.now());
      const { response, text } = await this.fetchText(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Handlekurven kunne ikke lastes inn. Prøv igjen.');
      this.render(text, celebrate);
      return this.normalizeGift();
    }
    async reconcile(response) {
      if (response.sections?.[this.section]) this.render(response.sections[this.section]);
      else await this.refresh();
      await this.normalizeProtection();
      await this.normalizeGift();
    }
    async normalizeGift() {
      const quantity = Number(this.content.dataset.discountQuantity || 0);
      const variant = Number(this.content.dataset.giftVariant || 0);
      const available = this.content.dataset.giftAvailable === 'true';
      const lines = JSON.parse(this.content.dataset.giftLines || '[]');
      const eligible = quantity >= 4 && variant && available;
      const updates = {};
      lines.forEach((line, index) => {
        const wanted = eligible && index === 0 ? 1 : 0;
        if (line.quantity !== wanted) updates[line.key] = wanted;
      });
      let result = null;
      if (Object.keys(updates).length) {
        result = await this.request('cart/update.js', { updates });
      } else if (eligible && !lines.length) {
        result = await this.request('cart/add.js', { items: [{ id: variant, quantity: 1, properties: { _promotion_gift: 'true' } }] });
      }
      if (result) {
        if (result.sections?.[this.section]) this.render(result.sections[this.section], false);
        else {
          const url = new URL(location.pathname, location.origin);
          url.searchParams.set('section_id', this.section);
          const { response, text } = await this.fetchText(url, { credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok) throw new Error('Handlekurven kunne ikke oppdateres. Prøv igjen.');
          this.render(text, false);
        }
        if (!Array.isArray(result.items) || result.item_count === undefined) result = await this.request('cart.js');
      }
      return result;
    }
    async normalizeProtection() {
      // Remove shipping protection left in carts from before the option was retired.
      const updates = Object.fromEntries(this.protectionLines.map(item => [item.key, 0]));
      if (Object.keys(updates).length) {
        const result = await this.request('cart/update.js', { updates });
        if (result.sections?.[this.section]) this.render(result.sections[this.section]);
        else await this.refresh();
      }
    }
    captureCartRows() {
      const rows = new Map();
      for (const row of this.content.querySelectorAll('.mc__item[data-mc-identity]')) {
        const identity = row.dataset.mcIdentity;
        const current = {
          quantity: Number(row.dataset.quantity),
          row: this.captureItemAnimation(row),
          image: this.captureItemAnimation(row.querySelector('.mc__image'))
        };
        rows.set(identity, current);
      }
      return rows;
    }
    captureItemAnimation(element) {
      if (!element) return null;
      const animation = element.getAnimations().find(item => item.id.startsWith('mc-item-') && item.playState === 'running');
      if (!animation) return null;
      const style = getComputedStyle(element);
      const state = {
        from: { opacity: style.opacity, transform: style.transform, backgroundColor: style.backgroundColor },
        duration: Math.max(0, Number(animation.effect.getTiming().duration) - Number(animation.currentTime || 0))
      };
      animation.cancel();
      return state;
    }
    animateCartRows(previousRows) {
      for (const row of this.content.querySelectorAll('.mc__item[data-mc-identity]')) {
        const previous = previousRows.get(row.dataset.mcIdentity);
        const added = previous && Number(row.dataset.quantity) > previous.quantity;
        const isNew = !previous;
        const rowCarry = previous?.row;
        const imageCarry = previous?.image;
        if (!isNew && !added && !rowCarry && !imageCarry) continue;
        const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
        if (rowCarry?.duration > 20 || isNew || added) {
          const from = rowCarry?.duration > 20 ? rowCarry.from : isNew
            ? { opacity: 0, transform: 'translateY(14px)', backgroundColor: 'transparent' }
            : { opacity: 1, transform: 'none', backgroundColor: '#e8f6ee' };
          const duration = rowCarry?.duration > 20 ? rowCarry.duration : isNew ? 430 : 650;
          const animation = row.animate([from, { opacity: 1, transform: 'none', backgroundColor: 'transparent' }], { duration, easing });
          animation.id = 'mc-item-row';
        }
        const image = row.querySelector('.mc__image');
        if (image && (imageCarry?.duration > 20 || isNew || added)) {
          const from = imageCarry?.duration > 20 ? imageCarry.from
            : { opacity: isNew ? 0.7 : 1, transform: 'scale(0.94)', backgroundColor: 'transparent' };
          const animation = image.animate([from, { opacity: 1, transform: 'scale(1)', backgroundColor: 'transparent' }], {
            duration: imageCarry?.duration > 20 ? imageCarry.duration : 500,
            easing
          });
          animation.id = 'mc-item-image';
        }
      }
    }
    render(html, celebrate = true) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('[data-mc-content]');
      if (!next) throw new Error('Handlekurven kunne ikke vises. Last siden på nytt.');
      const active = document.activeElement;
      const key = active?.closest('[data-key]')?.dataset.key;
      const focusAttribute = ['data-mc-quantity', 'data-mc-remove', 'data-mc-remove-code'].find(name => active?.hasAttribute(name));
      const focusValue = focusAttribute ? active.getAttribute(focusAttribute) : '';
      const scrollTop = this.scroller.scrollTop;
      const oldProgress = this.querySelector('.mc__progress > span');
      const trackWidth = oldProgress?.parentElement.getBoundingClientRect().width || 0;
      const previousProgress = trackWidth ? oldProgress.getBoundingClientRect().width / trackWidth * 100 : 0;
      const previousCartRows = this.captureCartRows();
      const previousMilestones = new Map([...this.content.querySelectorAll('[data-mc-milestone]')].map(marker => [marker.dataset.mcMilestone, marker.style.left]));
      oldProgress?.getAnimations().forEach(animation => animation.cancel());
      this.mcGroupPaidDuplicates?.(next);
      const oldQuantityFill = this.querySelector('.cart-quantity-promotion__track > span');
      const oldQuantityTrackWidth = oldQuantityFill?.parentElement.getBoundingClientRect().width || 0;
      const previousQuantityProgress = oldQuantityTrackWidth ? oldQuantityFill.getBoundingClientRect().width / oldQuantityTrackWidth : 0;
      oldQuantityFill?.getAnimations().forEach(animation => animation.cancel());
      const currentHint = this.querySelector('[data-mc-promotion-hint]');
      const nextHint = parsed.querySelector('[data-mc-promotion-hint]');
      if (currentHint && nextHint) currentHint.replaceWith(nextHint);
      else if (currentHint) currentHint.remove();
      else if (nextHint) this.querySelector('[data-mc-timer]').after(nextHint);
      const timerSignature = content => [...content.querySelectorAll('.mc__item[data-key]')]
        .map(row => `${row.dataset.key}:${row.dataset.quantity}`).sort().join('|');
      const cartChanged = timerSignature(this.content) !== timerSignature(next);
      this.content.replaceWith(next);
      const quantityFill = this.querySelector('.cart-quantity-promotion__track > span');
      if (quantityFill && oldQuantityFill && this.dialog.open && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const trackWidth = quantityFill.parentElement.getBoundingClientRect().width;
        const fromWidth = previousQuantityProgress * trackWidth;
        const toWidth = quantityFill.getBoundingClientRect().width;
        if (Math.abs(toWidth - fromWidth) > 0.5) {
          quantityFill.animate([{ width: fromWidth + 'px' }, { width: toWidth + 'px' }], {
            duration: 700,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
          });
        }
      }
      this.showPendingQuantities();
      const headingCount = this.querySelector('[data-mc-heading-count]');
      if (headingCount) headingCount.textContent = next.dataset.merchandise;
      this.scroller.scrollTop = scrollTop;
      if (this.dialog.open && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.content.querySelectorAll('[data-mc-milestone]').forEach(marker => {
          const previous = previousMilestones.get(marker.dataset.mcMilestone);
          if (previous && previous !== marker.style.left) marker.animate([{ left: previous }, { left: marker.style.left }], { duration: 500, easing: 'ease-out' });
          else if (!previous) marker.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 500 });
          if (Number(marker.dataset.mcMilestone) > this.promotionLevel * 3 && marker.classList.contains('is-unlocked')) {
            marker.querySelector('.mc__gifts').animate([{ transform: 'scale(1)' }, { transform: 'scale(1.05)' }, { transform: 'scale(1)' }], { duration: 400 });
          }
        });
        const progress = this.querySelector('.mc__progress > span');
        if (progress && Math.abs(parseFloat(progress.style.width) - previousProgress) > 0.1) {
          progress.animate([{ width: `${previousProgress}%` }, { width: progress.style.width }], {
            duration: 850,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
          });
        }
        this.animateCartRows(previousCartRows);
      }
      this.setBusy(this.busy);
      const promotionLevel = Math.min(3, Math.max(0, Number(this.content.dataset.discountQuantity || 0) - 1));
      if (promotionLevel > this.promotionLevel && celebrate) this.confetti();
      this.promotionLevel = promotionLevel;
      const count = this.content.dataset.count;
      document.querySelectorAll('.cart-link__bubble-num,[data-cart-count]').forEach(element => { element.textContent = count; });
      document.querySelectorAll('.cart-link__bubble').forEach(element => element.classList.toggle('cart-link__bubble--visible', Number(count) > 0));
      this.querySelector('[data-mc-status]').textContent = Number(count) ? this.dataset.updatedMessage : this.dataset.emptyMessage;
      this.startTimer(cartChanged);
      if (focusAttribute) {
        this.pendingFocus = () => {
          const scope = key ? this.querySelector(`[data-key="${CSS.escape(key)}"]`) : this;
          const control = scope?.querySelector(`[${focusAttribute}="${CSS.escape(focusValue)}"]`);
          (control || this.querySelector('[data-mc-close]')).focus({ preventScroll: true });
        };
      }
    }
    handleClick(event) {
      const target = event.target.closest('button,a');
      if (!target) return;
      if (target.hasAttribute('data-mc-close')) { this.close(); return; }
      if (this.busy) { event.preventDefault(); return; }
      if (target.hasAttribute('data-mc-checkout')) {
        event.preventDefault();
        if (this.quantityChanges.size) return;
        const href = target.href;
        this.run(async () => { await this.refresh(false); location.assign(href); });
      } else if (target.hasAttribute('data-mc-related-add')) {
        event.preventDefault();
        const card = target.closest('[data-mc-related-card]');
        const id = Number(card.querySelector('[data-mc-related-variant]')?.value || target.dataset.mcRelatedAdd);
        this.run(async () => {
          await this.reconcile(await this.request('cart/add.js', { items: [{ id, quantity: 1 }] }));
          this.pendingFocus = () => (this.querySelector('.mc-related__options summary, .mc-related__actions > [data-mc-related-add]') || this.querySelector('[data-mc-checkout]'))?.focus({ preventScroll: true });
        });
      } else if (target.hasAttribute('data-mc-quantity') || target.hasAttribute('data-mc-remove')) {
        const item = target.closest('[data-key]');
        const quantity = target.hasAttribute('data-mc-remove') ? 0 : Math.max(0, Number(item.dataset.quantity) + Number(target.dataset.mcQuantity));
        this.run(async () => this.reconcile(await this.request('cart/change.js', { id: item.dataset.key, quantity })))
          .then(() => { this.pendingFocus?.(); this.pendingFocus = null; });
      } else if (target.hasAttribute('data-mc-remove-code')) {
        this.run(async () => {
          const codes = [...this.querySelectorAll('[data-mc-remove-code]')].map(element => element.dataset.mcRemoveCode).filter(code => code !== target.dataset.mcRemoveCode);
          await this.reconcile(await this.request('cart/update.js', { discount: codes.join(',') }));
        });
      }
    }
    startTimer(restart = false) {
      clearInterval(this.timer);
      const bar = this.querySelector('[data-mc-timer]');
      if (!bar) return;
      const storageKey = 'matilde-cart-deadline-599-v2';
      const duration = 599000;
      if (!Number(this.content.dataset.merchandise)) {
        bar.hidden = true;
        this.deadline = null;
        try { sessionStorage.removeItem(storageKey); } catch (_) {}
        return;
      }
      if (restart) this.deadline = Date.now() + duration;
      if (!this.deadline) {
        try { this.deadline = Number(sessionStorage.getItem(storageKey)) || Date.now() + duration; }
        catch (_) { this.deadline = Date.now() + duration; }
      }
      try { sessionStorage.setItem(storageKey, this.deadline); } catch (_) {}
      const active = bar.querySelector('[data-mc-timer-active]');
      const expired = bar.querySelector('[data-mc-timer-expired]');
      const icon = bar.querySelector('svg');
      const clock = bar.querySelector('[data-mc-clock]');
      const tick = () => {
        const seconds = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
        bar.hidden = false;
        active.hidden = seconds === 0;
        expired.hidden = seconds > 0;
        if (icon) icon.style.display = seconds ? '' : 'none';
        clock.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
        if (!seconds) clearInterval(this.timer);
      };
      tick();
      if (this.dialog.open && this.deadline > Date.now()) this.timer = setInterval(tick, 1000);
    }
    confetti() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !this.dialog.open) return;
      cancelAnimationFrame(this.animation);
      const canvas = this.querySelector('canvas');
      const ctx = canvas.getContext('2d');
      const width = this.dialog.clientWidth, height = this.dialog.clientHeight;
      const ratio = Math.min(devicePixelRatio || 1, 2);
      canvas.width = width * ratio; canvas.height = height * ratio;
      ctx.scale(ratio, ratio);
      const colors = ['#00D864', '#ffffff', '#e9e9e9', '#111111'];
      const pieces = Array.from({ length: 160 }, () => ({ x: Math.random() * width, y: -Math.random() * height * .5, speed: 2 + Math.random() * 4, drift: Math.random() * 2 - 1, angle: Math.random() * 6, color: colors[Math.floor(Math.random() * colors.length)] }));
      const start = performance.now();
      const frame = now => {
        ctx.clearRect(0, 0, width, height);
        if (now - start > 3200 || !this.dialog.open) return;
        ctx.globalAlpha = Math.min(1, (3200 - (now - start)) / 700);
        for (const piece of pieces) {
          piece.y += piece.speed; piece.x += piece.drift; piece.angle += .045;
          ctx.save(); ctx.translate(piece.x, piece.y); ctx.rotate(piece.angle); ctx.fillStyle = piece.color; ctx.fillRect(-3, -5, 6, 10); ctx.restore();
        }
        this.animation = requestAnimationFrame(frame);
      };
      this.dispatchEvent(new CustomEvent('matilde-cart:celebrate', { bubbles: true }));
      this.animation = requestAnimationFrame(frame);
    }
  }
  customElements.define('matilde-cart', MatildeCart);
})();

